import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux'
import { change, getFormValues } from 'redux-form'
// constants
import { Forms } from 'constants/index'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useApolloClient } from 'hooks/useApolloClient'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
// types
import { IFieldLink } from 'types/graphql'
import { RiskNotificationType } from 'types/risk'
// graphql
import { APP_NOTIFICATION } from 'graphql/notifications/subscriptions'
// utils
import { uniq } from 'lodash'
import { getTableCells } from 'utils/form/risk'

const getLinkedCells = (formValues: Record<string, any>, fieldLinks: IFieldLink[]) =>
  getTableCells(
    formValues,
    fieldLinks.map(({ id }) => id)
  ).filter(({ cell }) => cell?.linkedRiskId)

/**
 * Listens to SetField notifications of the linked risks shown in the form tables
 * and puts the new value into every cell that references the changed field value.
 * The value is persisted to the linked risk by useSaveFieldValue of the cell.
 */
export const useLinkedRisksNotifications = (fieldLinks: IFieldLink[] = []) => {
  const user = useCurrentUser()
  const store = useStore()
  const router = useRouter()
  const dispatch = useDispatch()

  const { client } = useApolloClient()

  const isPublic = React.useContext(PublicContext)

  const domain = isPublic ? router.query.domain : user.company.domainName

  // linkedRiskId -> its notification subscription
  const subscriptionsRef = React.useRef(new Map<string, { unsubscribe: () => void }>())

  // Only the primary cells: removing a row removes its primary cell, while the cells of its secondary
  // columns stay in the form values. shallowEqual keeps the same array between unrelated form changes
  const linkedRiskIds = useSelector((state): string[] => {
    const formValues = getFormValues(Forms.RISK_TEMPLATE_FORM)(state)
    const primaryFieldLinks = fieldLinks.filter(({ primary }) => primary)

    return uniq(getLinkedCells(formValues, primaryFieldLinks).map(({ cell }) => cell.linkedRiskId))
  }, shallowEqual)

  // drop every subscription when the handler inputs change (and on unmount), the effect below opens them again
  React.useEffect(() => {
    const subscriptions = subscriptionsRef.current

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe())
      subscriptions.clear()
    }
  }, [fieldLinks, domain, client])

  React.useEffect(() => {
    const subscriptions = subscriptionsRef.current

    const applyNotification = (notification: any) => {
      if (notification?.notificationType !== RiskNotificationType.SetField || !notification.fieldValueId) {
        return
      }

      const formValues = getFormValues(Forms.RISK_TEMPLATE_FORM)(store.getState())

      getLinkedCells(formValues, fieldLinks)
        .filter(({ cell }) => cell.linkedFieldValueId === notification.fieldValueId)
        .forEach(({ path, cell }) =>
          dispatch(change(Forms.RISK_TEMPLATE_FORM, path, { ...cell, value: notification.fieldValue }))
        )
    }

    subscriptions.forEach((subscription, id) => {
      if (!linkedRiskIds.includes(id)) {
        subscription.unsubscribe()
        subscriptions.delete(id)
      }
    })

    linkedRiskIds
      .filter((id) => !subscriptions.has(id))
      .forEach((id) => {
        const subscription = client
          .subscribe({ query: APP_NOTIFICATION, variables: { eventType: `${id}:${domain}` } })
          .subscribe({
            next: ({ data }: any) => applyNotification(data?.notification?.payload),
            error: (err) => console.error('[useLinkedRisksNotifications]: ', err)
          })

        subscriptions.set(id, subscription)
      })
  }, [linkedRiskIds, fieldLinks, domain, client])
}
